import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Link from '@/routing/Link'
import {
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  Loader2,
} from '@digital-twins/design-system/icons'
import {
  KnowledgeAskCitation,
  KnowledgeDocumentSocialInfo,
  SocialCitationPost,
} from '@digital-twins/shared-types'
import { AssistantPageRegion } from '@digital-twins/shared-types/assistant'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Text } from '@/components/ui/Text'
import { knowledgeApi } from '@/lib/api'
import { WebsitePreview, isEmbeddableHttpUrl } from '../KnowledgeDocumentPage/WebsitePreview'
import {
  documentDescription,
  documentTitle,
} from '../KnowledgeHubPage/libraryHelpers'
import { VideoPreview } from '../KnowledgeDocumentPage/VideoPreview'
import { isKnowledgeAudioFile, isKnowledgeMediaFile, isKnowledgeVideoUrl, knowledgeVideoUrlAtTime } from '../KnowledgeHubPage/videoUrl'
import { toast } from 'sonner'
import i18n from '@/i18n'
import { citationSocialPost } from '../KnowledgeHubPage/social/SocialCitationPostMeta'
import { SocialCitationSource } from '../KnowledgeHubPage/social/SocialCitationSource'
import { SocialCitationDigest } from '../KnowledgeHubPage/social/SocialCitationDigest'
import { formatSocialCount, formatSocialDate } from '../KnowledgeHubPage/social/socialFormat'
import { ROUTES } from '@/routes/routes'
import { readableQuote } from '@/components/citations'
import { isCitationUuid } from './injectCitationMarkers'
import { PdfPassagePage } from './PdfPassagePage'
import { hasRegion } from './partitionAssistantCitations'
import {
  estimatePassageBand,
  findHighlightedPassage,
  HighlightedPassageParts,
  ResolvedPassageOffsets,
} from './highlightPassageInText'
import {
  ANCHOR_MATCH_CONFIDENCE_THRESHOLD,
  CitationVerification,
  citationVerification,
} from './citationVerification'

/**
 * One document's evidence view: the cited page (raster and/or text), the quoted
 * passage, and what we can prove about the link between them.
 *
 * Rendered inside a tab of ThreadSourcePanel, one instance per open document.
 * Instances stay mounted while their tab is hidden so switching tabs does not
 * refetch the page preview or lose scroll position.
 */

interface ThreadSourceViewProps {
  citationIndex: number | null
  citation: KnowledgeAskCitation | null
  documentLabel: string
  onReportWrongPage?: (citation: KnowledgeAskCitation) => void
  /** Reports the real filename once resolved, so the tab can relabel itself. */
  onResolveTitle?: (title: string) => void
}

/**
 * The title reads the way the library reads it, not the way the file was named.
 * `documentTitle` is the same helper the hub cards and the sources list use, so
 * one document is called one thing everywhere; the raw filename is only a last
 * resort for a document we could not fetch.
 */
function humanDocumentTitle(
  documentLabel: string,
  citation: KnowledgeAskCitation | null,
  resolvedTitle: string | null,
): string {
  if (resolvedTitle) return resolvedTitle
  const candidates = [documentLabel, citation?.documentName || '']
  for (const candidate of candidates) {
    const name = candidate.trim()
    if (name && name.toLowerCase() !== 'source' && !isCitationUuid(name)) {
      return name
    }
  }
  return i18n.t('library.facet.source', { ns: 'app' })
}

function timestampFromChapterText(text: string | null | undefined): string | null {
  const match = (text || '').match(/^#{2,3}\s*\[(\d{1,2}:\d{2}(?::\d{2})?)\]/m)
  return match?.[1] ?? null
}

function toImageSrc(previewImage: string): string {
  if (previewImage.startsWith('http') || previewImage.startsWith('data:')) {
    return previewImage
  }
  const mime = previewImage.startsWith('iVBOR') ? 'png' : 'jpeg'
  return `data:image/${mime};base64,${previewImage}`
}

/** Placeholder that occupies roughly a page while the source is being located
 * and fetched, so opening a citation never shows an empty panel. */
function PreviewLoading() {
  const { t } = useTranslation('app')

  return (
    <div
      className="flex min-h-[420px] items-center justify-center gap-2 rounded-xl border border-border bg-muted/20"
      role="status"
      aria-live="polite"
      data-testid="thread-source-preview-loading"
    >
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />
      <Text size="sm" color="muted">
        {t('threads.detail.findingSource')}
      </Text>
    </div>
  )
}

function NeighbourContext({
  label,
  text,
  testId,
}: {
  label: string
  text: string
  testId: string
}) {
  return (
    <div data-testid={testId}>
      <Text size="xs" color="muted" className="mb-1">
        {label}
      </Text>
      <pre className="whitespace-pre-wrap rounded-xl border border-dashed border-border/60 bg-muted/20 p-3 text-xs leading-relaxed text-muted-foreground">
        {text}
      </pre>
    </div>
  )
}

function PagePreviewText({
  text,
  highlighted,
  pageNumber,
  previousPageTail,
  nextPageHead,
}: {
  text: string
  highlighted: HighlightedPassageParts | null
  pageNumber: number | null
  previousPageTail?: string | null
  nextPageHead?: string | null
}) {
  const { t } = useTranslation('app')

  const markRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    markRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlighted])

  return (
    <div className="space-y-2">
      {previousPageTail ? (
        <NeighbourContext
          label={
            pageNumber != null
              ? t('threads.detail.endOfPage', { n: pageNumber - 1 })
              : t('threads.detail.endOfPreviousPage')
          }
          text={`…${previousPageTail}`}
          testId="thread-source-previous-context"
        />
      ) : null}
      <pre className="whitespace-pre-wrap rounded-xl border border-border bg-muted/30 p-4 text-xs leading-relaxed">
        {highlighted ? (
          <>
            {highlighted.before}
            <mark
              ref={markRef}
              className="rounded-sm bg-foreground/15 px-0.5 text-foreground"
              data-testid="thread-source-passage-highlight"
            >
              {highlighted.match}
            </mark>
            {highlighted.after}
          </>
        ) : (
          text
        )}
      </pre>
      {nextPageHead ? (
        <NeighbourContext
          label={
            pageNumber != null
              ? t('threads.detail.startOfPage', { n: pageNumber + 1 })
              : t('threads.detail.startOfNextPage')
          }
          text={`${nextPageHead}…`}
          testId="thread-source-next-context"
        />
      ) : null}
    </div>
  )
}

function PagePreviewImage({
  src,
  pageNumber,
  pageText,
  snippet,
  offsets,
  region,
}: {
  src: string
  pageNumber: number
  pageText: string
  snippet: string
  offsets?: ResolvedPassageOffsets | null
  /** A cited page region: drawn exactly, instead of estimating a band from the snippet. */
  region?: AssistantPageRegion | null
}) {
  const { t } = useTranslation('app')

  const band = useMemo(
    () => region
      ? { topPct: region.y * 100, heightPct: region.height * 100, leftPct: region.x * 100, widthPct: region.width * 100 }
      : estimatePassageBand(pageText, snippet, offsets),
    [pageText, snippet, offsets, region],
  )
  const bandColumns = band && 'leftPct' in band
    ? { left: `${band.leftPct}%`, width: `${band.widthPct}%` }
    : undefined
  const bandRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bandRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [src, band?.topPct])

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border bg-muted/20"
      data-testid="thread-source-page-image"
    >
      {/* Decorative when the same page is also rendered as text below: the
          section heading already names the page, so a repeated alt only makes
          screen readers and copied selections say "Page N" twice. */}
      <img
        src={src}
        alt={pageText ? '' : t('library.preview.pageAlt', { n: pageNumber })}
        className="block w-full"
      />
      {band ? (
        <div
          ref={bandRef}
          className={cn(
            'pointer-events-none absolute rounded-md border-2 border-foreground/50 bg-foreground/10 shadow-[0_0_0_1px_rgba(0,0,0,0.04)]',
            !bandColumns && 'inset-x-2',
          )}
          style={{
            top: `${band.topPct}%`,
            height: `${band.heightPct}%`,
            ...bandColumns,
          }}
          data-testid="thread-source-page-highlight-band"
          aria-hidden
        />
      ) : null}
    </div>
  )
}

export function ThreadSourceView({
  citationIndex,
  citation,
  documentLabel,
  onReportWrongPage,
  onResolveTitle,
}: ThreadSourceViewProps) {
  const { t } = useTranslation('app')

  const [previewText, setPreviewText] = useState<string | null>(null)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [previousPageTail, setPreviousPageTail] = useState<string | null>(null)
  const [nextPageHead, setNextPageHead] = useState<string | null>(null)
  // Undefined means not resolved yet; null is an authoritative unresolved
  // result and must not revive a historical citation page.
  const [resolvedPage, setResolvedPage] = useState<number | null | undefined>(undefined)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [resolvedTitle, setResolvedTitle] = useState<string | null>(null)
  /** The library's own description of the document, for the overview section. */
  const [overview, setOverview] = useState<string | null>(null)
  const [overviewOpen, setOverviewOpen] = useState(false)
  const [displaySnippet, setDisplaySnippet] = useState<string>('')
  const [locatedConfidence, setLocatedConfidence] = useState<number | null>(
    null,
  )
  const [isPdf, setIsPdf] = useState(false)
  const [pdfUnavailable, setPdfUnavailable] = useState(false)
  const [sourceUrl, setSourceUrl] = useState<string | null>(null)
  const [linkThumbnailUrl, setLinkThumbnailUrl] = useState<string | null>(null)
  const [locatedTimestamp, setLocatedTimestamp] = useState<string | null>(null)
  const [uploadedMediaSrc, setUploadedMediaSrc] = useState<string | null>(null)
  const [isUploadedMedia, setIsUploadedMedia] = useState(false)
  const [isAudioSource, setIsAudioSource] = useState(false)
  // Until the document's type is known we cannot choose a renderer. Committing
  // early would flash the text fallback and then swap it for the PDF.
  const [documentResolved, setDocumentResolved] = useState(false)
  // Typed social data: the digest's channel metadata, and the posts locate
  // resolved. Together they decide the social views; the digest text never does.
  const [socialInfo, setSocialInfo] = useState<KnowledgeDocumentSocialInfo | null>(null)
  const [locatedPost, setLocatedPost] = useState<SocialCitationPost | null>(null)
  const [locatedPosts, setLocatedPosts] = useState<Array<SocialCitationPost>>([])

  const documentToken =
    citation?.documentToken ||
    (citation?.documentName && isCitationUuid(citation.documentName)
      ? citation.documentName
      : null)

  // Prefer a resolved page; fall back to any stored page hint even if marked unknown.
  const citationPage =
    citation?.pageNumber != null && Number.isFinite(citation.pageNumber)
      ? citation.pageNumber
      : null

  const pageNumber = resolvedPage === undefined ? citationPage : resolvedPage

  const title = humanDocumentTitle(documentLabel, citation, resolvedTitle)

  useEffect(() => {
    if (!documentToken) {
      setDocumentResolved(true)
      setSourceUrl(null)
      setLinkThumbnailUrl(null)
      setIsUploadedMedia(false)
      setIsAudioSource(false)
      setSocialInfo(null)
      return
    }
    let cancelled = false
    setDocumentResolved(false)
    setSocialInfo(null)
    setOverview(null)
    setSourceUrl(null)
    setLinkThumbnailUrl(null)
    setIsUploadedMedia(false)
    setIsAudioSource(false)
    void (async () => {
      try {
        const doc = await knowledgeApi.getDocument(documentToken)
        if (cancelled) return
        const cleanTitle = documentTitle(doc)
        if (cleanTitle) {
          setResolvedTitle(cleanTitle)
          onResolveTitle?.(cleanTitle)
        }
        setOverview(documentDescription(doc))
        setSocialInfo(doc.social ?? null)
        const nextUrl = doc.sourceUrl?.trim() || null
        setSourceUrl(
          nextUrl && isEmbeddableHttpUrl(nextUrl) ? nextUrl : null,
        )
        setLinkThumbnailUrl(doc.linkThumbnailUrl?.trim() || null)
        setIsUploadedMedia(
          isKnowledgeMediaFile(doc.mimeType, doc.originalFilename),
        )
        setIsAudioSource(
          isKnowledgeAudioFile(doc.mimeType, doc.originalFilename),
        )
        setIsPdf(
          doc.mimeType === 'application/pdf' ||
            Boolean(doc.originalFilename?.toLowerCase().endsWith('.pdf')),
        )
      } catch {
        // Keep fallback title from props, and treat it as a non-PDF so the
        // page still renders from the preview endpoint.
      } finally {
        if (!cancelled) setDocumentResolved(true)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callback identity must not refetch
  }, [documentToken])

  useEffect(() => {
    // The readable passage, the same one the citation card quoted: shown,
    // located and highlighted as is, never swapped for the page's raw text.
    const rawSnippet = (citation?.snippet || '').trim()
    const snippet = readableQuote(rawSnippet) ?? ''
    // Display formatting may omit tables or join excerpts. It must not
    // replace the complete source passage used to establish provenance.
    const locateText = rawSnippet
    setDisplaySnippet(snippet)
    setLocatedConfidence(null)
    setLocatedTimestamp(citation?.timestampLabel?.trim() || null)
    setPreviousPageTail(null)
    setNextPageHead(null)
    setPdfUnavailable(false)
    setLocatedPost(null)
    setLocatedPosts([])

    if (!documentToken) {
      setPreviewText(null)
      setPreviewImage(null)
      setResolvedPage(null)
      return
    }

    let cancelled = false
    setLoadingPreview(true)
    void (async () => {
      try {
        let page = citationPage
        let pageTextHint = ''

        // Always try locate when we have a snippet — improves old answers and
        // upgrades weak/unknown page hints to a better matched page.
        if (locateText) {
          try {
            const located = await knowledgeApi.locateSnippet(
              documentToken,
              locateText,
            )
            if (cancelled) return
            // A social digest names its posts: nothing else about the page is
            // needed, so no page preview is fetched for it.
            if (located.post || (located.posts?.length ?? 0) > 0) {
              setLocatedPost(located.post ?? null)
              setLocatedPosts(located.posts ?? [])
              return
            }
            // A fresh typed resolution supersedes a saved page hint, including
            // an explicit unresolved result for a multi-page passage.
            page = located.pageUnknown ? null : located.pageNumber
            if (page != null) {
              pageTextHint = located.text || ''
              setLocatedConfidence(Number(located.anchorConfidence ?? 0))
              if (located.matchedSnippet && !snippet) {
                setDisplaySnippet(readableQuote(located.matchedSnippet) ?? '')
              }
              if (located.timestampLabel) {
                setLocatedTimestamp(located.timestampLabel)
              } else {
                const fromChapter = timestampFromChapterText(located.text)
                if (fromChapter) {
                  setLocatedTimestamp(fromChapter)
                }
              }
            }
          } catch {
            // Fall through — may still open citationPage hint below.
          }
        }

        setResolvedPage(page)

        if (page == null) {
          if (!cancelled) {
            setPreviewText(null)
            setPreviewImage(null)
          }
          return
        }

        const preview = await knowledgeApi.getPagePreview(documentToken, page)
        if (cancelled) return
        const chapterText = preview.text || pageTextHint || null
        setPreviewText(chapterText)
        setPreviewImage(preview.imageUrl || preview.imageBase64 || null)
        setPreviousPageTail(preview.previousPageTail || null)
        setNextPageHead(preview.nextPageHead || null)
        setLocatedTimestamp((current) => current || timestampFromChapterText(chapterText))
      } catch {
        if (!cancelled) {
          setPreviewText(null)
          setPreviewImage(null)
          setPreviousPageTail(null)
          setNextPageHead(null)
          // Keep the canonical resolution; preview failure cannot restore an
          // older page hint that the resolver has superseded.
        }
      } finally {
        if (!cancelled) setLoadingPreview(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [documentToken, citationPage, citation?.snippet, citation?.pageUnknown, citation?.timestampLabel])

  useEffect(() => {
    if (!documentToken || !isUploadedMedia) {
      return
    }
    let objectUrl: string | null = null
    let cancelled = false
    void knowledgeApi
      .downloadDocument(documentToken)
      .then(({ downloadUrl }) => {
        if (cancelled) {
          if (typeof URL.revokeObjectURL === 'function') {
            URL.revokeObjectURL(downloadUrl)
          }
          return
        }
        objectUrl = downloadUrl
        setUploadedMediaSrc(downloadUrl)
      })
      .catch(() => {
        if (!cancelled) {
          setUploadedMediaSrc(null)
        }
      })
    return () => {
      cancelled = true
      if (objectUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl)
      }
      setUploadedMediaSrc(null)
    }
  }, [documentToken, isUploadedMedia])

  async function handleDownload() {
    if (!documentToken) return
    setDownloading(true)
    try {
      const { downloadUrl, filename } = await knowledgeApi.downloadDocument(
        documentToken,
      )
      if (downloadUrl) {
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename || 'document'
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1_000)
      } else {
        toast.error(t('threads.detail.downloadUnavailable'))
      }
    } catch {
      toast.error(t('threads.detail.downloadFailed'))
    } finally {
      setDownloading(false)
    }
  }

  const timestampLabel =
    locatedTimestamp || citation?.timestampLabel?.trim() || null
  const isVideoSource =
    Boolean(sourceUrl && isKnowledgeVideoUrl(sourceUrl)) || isUploadedMedia
  const videoPlayerUrl = uploadedMediaSrc || sourceUrl
  const videoWatchUrl =
    sourceUrl && timestampLabel
      ? knowledgeVideoUrlAtTime(sourceUrl, timestampLabel)
      : sourceUrl

  const snippetForHighlight = displaySnippet || citation?.snippet || ''
  // A page_region citation boxes its region in whichever renderer shows the page.
  const citationRegion = hasRegion(citation) ? citation.region : null

  // Server-resolved offsets address the citation's own page. If the preview
  // resolved to a different page they would point at unrelated text, so drop
  // them and let the text match decide.
  const passageOffsets: ResolvedPassageOffsets | null =
    citation && pageNumber != null && pageNumber === citationPage
      ? {
          unitCharStart: citation.unitCharStart ?? null,
          unitCharEnd: citation.unitCharEnd ?? null,
        }
      : null

  // Opening the panel re-runs locate against the current extraction, so it can
  // upgrade a citation that was stored before the document was fully segmented.
  // Prefer that live result; fall back to the anchor stored with the answer.
  const verification: CitationVerification =
    pageNumber == null
      ? 'unlocated'
      : locatedConfidence != null
        ? locatedConfidence >= ANCHOR_MATCH_CONFIDENCE_THRESHOLD
          ? 'matched'
          : 'page_only'
        : citationVerification(citation)

  // Where the passage sits in the page's own text, if we can find it.
  const pageHighlight: HighlightedPassageParts | null = previewText
    ? findHighlightedPassage(previewText, snippetForHighlight, passageOffsets)
    : null

  // The original PDF gives glyph-accurate highlighting, so prefer it over the
  // rasterised page whenever we can get it; `pdfUnavailable` flips on the first
  // failure (not a PDF after all, blocked cross-origin read, scanned page) and
  // hands the section back to the raster preview for the rest of this citation.
  const usePdfPage =
    isPdf && !pdfUnavailable && Boolean(documentToken) && pageNumber != null

  // One renderer at a time, best first: the original PDF, then the rasterised
  // page, then the extracted text. Showing the page and its transcript together
  // just makes the reader scroll past the same content twice.
  const renderer = usePdfPage
    ? 'pdf'
    : previewImage
      ? 'image'
      : previewText
        ? 'text'
        : 'none'

  // Deep links carry a page only when we actually located the passage: sending
  // a reader to a page we could not verify is worse than sending them to the
  // document and letting them look.
  const libraryHref = documentToken
    ? verification !== 'unlocated' && timestampLabel
      ? `/knowledge/${documentToken}?t=${encodeURIComponent(timestampLabel)}`
      : verification !== 'unlocated' && pageNumber != null
        ? `/knowledge/${documentToken}?page=${pageNumber}`
        : `/knowledge/${documentToken}`
    : null

  const savedSocialPost = citation ? citationSocialPost(citation) : null
  const socialPost = savedSocialPost ?? locatedPost

  // Until the document's type is known, a citation with no specific post
  // could be a social digest whose raw text must never show: hold briefly.
  if (documentToken && !documentResolved && !savedSocialPost) {
    return (
      <div className="flex min-h-0 flex-1 flex-col px-5 py-4" data-testid="thread-source-view">
        <PreviewLoading />
      </div>
    )
  }

  const citationLabel =
    citationIndex != null
      ? t('threads.detail.citationPage', { index: citationIndex })
      : t('threads.detail.citation')
  const topicHref = socialInfo?.topicToken
    ? ROUTES.knowledgeTopic(socialInfo.topicToken)
    : null

  if (socialPost) {
    return (
      <SocialCitationSource
        post={socialPost}
        citationLabel={citationLabel}
        topicHref={topicHref}
        snippet={citation?.snippet}
      />
    )
  }
  if (locatedPosts.length > 0) {
    return (
      <SocialCitationDigest
        topicName={
          socialInfo?.trackerName ??
          locatedPosts[0].topicName ??
          locatedPosts[0].trackerName ??
          title
        }
        citationLabel={citationLabel}
        topicHref={topicHref}
        posts={locatedPosts}
      />
    )
  }
  if (socialInfo) {
    const start = formatSocialDate(socialInfo.windowStart, i18n.language)
    const end = formatSocialDate(socialInfo.windowEnd, i18n.language)
    const windowText =
      start && end
        ? start === end
          ? start
          : t('socialListening.citation.digestWindow', { start, end })
        : (start ?? end ?? '')
    return (
      <SocialCitationDigest
        topicName={socialInfo.trackerName}
        citationLabel={citationLabel}
        topicHref={topicHref}
        countLine={
          loadingPreview
            ? null
            : t('socialListening.citation.digestCount', {
                count: socialInfo.itemCount,
                value: formatSocialCount(socialInfo.itemCount, i18n.language),
                window: windowText,
              })
        }
      >
        {loadingPreview ? <PreviewLoading /> : null}
      </SocialCitationDigest>
    )
  }

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      data-testid="thread-source-view"
    >
      <div className="space-y-3 border-b border-border px-5 py-4 text-left">
        <div className="min-w-0 space-y-1">
          {/* Three lines of budget before it clamps: library titles carry the
              year, the market and the report type, and one truncated line threw
              away exactly the part that tells two reports of the same series
              apart. The tooltip is the escape hatch for the rest, and opens
              fast because it is answering "which document is this?". */}
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <h2
                  className="line-clamp-3 font-serif text-xl font-normal leading-snug"
                  data-testid="thread-source-title"
                >
                  {title}
                </h2>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-xs"
                data-testid="thread-source-title-tooltip"
              >
                <p className="text-xs">{title}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <div className="flex flex-wrap items-center gap-2">
            <Text size="xs" color="muted">
              {citationIndex != null
                ? t('threads.detail.citationPage', { index: citationIndex })
                : t('threads.detail.citation')}
              {timestampLabel
                ? ` · ${timestampLabel}`
                : pageNumber != null && !sourceUrl
                  ? ` · page ${pageNumber}`
                  : ''}
            </Text>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {libraryHref ? (
            <Button asChild variant="outline" size="sm" className="h-8">
              <Link href={libraryHref} data-testid="thread-source-open-library">
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {t('threads.detail.openInLibrary')}
              </Link>
            </Button>
          ) : null}
          {sourceUrl ? (
            <Button asChild variant="outline" size="sm" className="h-8">
              <a
                href={videoWatchUrl || sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="thread-source-open-website"
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                {isVideoSource ? t('library.preview.openSource') : t('library.preview.openWebsite')}
              </a>
            </Button>
          ) : null}
          {documentToken ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              disabled={downloading}
              data-testid="thread-source-download"
              onClick={() => void handleDownload()}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {t('library.preview.download')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {overview ? (
          <section data-testid="thread-source-overview">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 text-left"
              aria-expanded={overviewOpen}
              data-testid="thread-source-overview-toggle"
              onClick={() => setOverviewOpen((current) => !current)}
            >
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
                  overviewOpen ? 'rotate-90' : null,
                )}
                aria-hidden
              />
              <Text size="xs" color="muted" className="font-medium">
                {t('threads.detail.documentOverview')}
              </Text>
            </button>
            {overviewOpen ? (
              <Text
                size="sm"
                color="muted"
                className="mt-2 leading-relaxed"
                data-testid="thread-source-overview-body"
              >
                {overview}
              </Text>
            ) : null}
          </section>
        ) : null}

        {displaySnippet ? (
          <section data-testid="thread-source-snippet">
            <Text size="xs" color="muted" className="mb-2 font-medium">
              {verification === 'matched'
                ? timestampLabel
                  ? t('threads.detail.citedPassageAt', { time: timestampLabel })
                  : t('threads.detail.citedPassage')
                : t('threads.detail.citedPassageQuoted')}
            </Text>
            <blockquote
              className={cn(
                'rounded-xl border border-muted-foreground/30 bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground',
                verification === 'unlocated' && 'border-dashed',
              )}
              data-testid="thread-source-cited-passage"
            >
              {displaySnippet}
            </blockquote>
          </section>
        ) : null}

        <section data-testid="thread-source-page-preview">
          <Text size="xs" color="muted" className="mb-2 font-medium">
            {isVideoSource
              ? timestampLabel
                ? isAudioSource
                  ? t('library.preview.audioAt', { time: timestampLabel })
                  : t('library.preview.videoAt', { time: timestampLabel })
                : isAudioSource
                  ? t('library.preview.audio')
                  : t('library.preview.video')
              : sourceUrl
                ? t('library.preview.website')
                : pageNumber != null
                  ? t('library.preview.pageAlt', { n: pageNumber })
                  : t('threads.detail.sourcePage')}
          </Text>
          {!documentResolved ? (
            <PreviewLoading />
          ) : isVideoSource && videoPlayerUrl ? (
            <VideoPreview
              url={videoPlayerUrl}
              title={title}
              transcript={previewText}
              startAt={timestampLabel}
              mediaKind={isAudioSource ? 'audio' : 'video'}
              hideOpenSource={!sourceUrl}
            />
          ) : sourceUrl ? (
            <WebsitePreview
              url={sourceUrl}
              title={title}
              thumbnailUrl={linkThumbnailUrl}
              fallbackText={previewText}
            />
          ) : loadingPreview ? (
            <PreviewLoading />
          ) : renderer === 'pdf' && documentToken && pageNumber != null ? (
            <PdfPassagePage
              key={`${documentToken}-${pageNumber}`}
              documentToken={documentToken}
              pageNumber={pageNumber}
              snippet={snippetForHighlight}
              region={citationRegion}
              onUnavailable={() => setPdfUnavailable(true)}
            />
          ) : renderer === 'image' && previewImage ? (
            <PagePreviewImage
              src={toImageSrc(previewImage)}
              pageNumber={pageNumber ?? 1}
              pageText={previewText || ''}
              snippet={snippetForHighlight}
              offsets={passageOffsets}
              region={citationRegion}
            />
          ) : renderer === 'text' && previewText ? (
            <div>
              <Text size="xs" color="muted" className="mb-2 font-medium">
                {pageHighlight
                  ? t('threads.detail.textOnPageHighlighted')
                  : t('threads.detail.textOnPage')}
              </Text>
              <PagePreviewText
                text={previewText}
                highlighted={pageHighlight}
                pageNumber={pageNumber}
                previousPageTail={previousPageTail}
                nextPageHead={nextPageHead}
              />
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-xl border border-border px-4 py-3">
              <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <Text size="sm" color="muted">
                {documentToken
                  ? t('threads.detail.passageMissing')
                  : t('threads.detail.noDocumentLinked')}
              </Text>
            </div>
          )}
        </section>

        {citation?.citationToken && onReportWrongPage ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 px-0 text-xs text-muted-foreground"
            data-testid="thread-source-wrong-page"
            onClick={() => onReportWrongPage(citation)}
          >
            {t('threads.detail.reportWrongPage')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}
